// SMS Attribution dashboard — campaign-level, vertical-level, and
// time-of-day analytics for the SMS dispatch + TrackDrive revenue engine.
//
// The auto-dispatch toggle + send-delay editor that used to live here (MIS-966)
// moved to the dedicated Auto Send page (/sms/auto-send, MIS-969). This page is
// now purely analytics — the "Form Auto Send" line appears in the per-campaign
// breakdown naturally once cache sends are tagged (no UI change needed).
//
// Controls (shared by the stat cards, charts AND the rollup tables):
//   - Vertical dropdown: "All verticals" + each campaign vertical (populated
//     from the response's byVertical rollup).
//   - Time selection: presets (15m … 30d) + a custom start/end date range.
// Both re-query on change. The whole page AUTO-UPDATES via polling (~15s) —
// stats + charts refresh in place, no page reload. Polling pauses while the
// tab is hidden (visibilitychange) to save cost.
//
// Resilience (MIS-966): the SMS stats route can 500, or return an empty/partial
// payload before any sends exist. The page NEVER blanks — it always renders the
// header + controls, then a clean loading skeleton, an error banner (showing the
// ApiError message), or a "No SMS data yet" empty state. Every nested array is
// read through a `?? []` guard so a missing series (e.g. byHourOfDay undefined)
// can't throw during render. There is no error boundary above this page, so a
// render-time throw would unmount the whole tree to a blank screen — which is
// exactly the bug this guard set fixes.
//
// Time convention: the existing stats methods (debtMix/leads) serialize a
// `TimeSel` to epoch SECONDS. `smsStats` follows the same seconds contract —
// `toTimeSel` resolves the preset/custom selection into a `TimeSel`, then
// `toSecondsRange` flattens it into the explicit { start, end } seconds pair
// the route expects. There is no milliseconds unit anywhere on this page.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ChartBar, { type BarPoint } from "../components/ChartBar";
import ChartLine, { type LinePoint } from "../components/ChartLine";
import {
  api,
  type SmsRollup,
  type SmsStatsResponse,
  type TimeSel,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;

// "Today" is the special sentinel: its range is [PT midnight today, now] and it
// is the DEFAULT selected preset on load (MIS-972). The other presets stay
// rolling-window seconds.
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT }, // special: since PT (America/Los_Angeles) midnight
  { label: "Last 15 min", sec: 15 * 60 },
  { label: "Last 30 min", sec: 30 * 60 },
  { label: "Last hour", sec: 3600 },
  { label: "Last 3 hours", sec: 3 * 3600 },
  { label: "Last 6 hours", sec: 6 * 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// Reserved pseudo-campaign id for the under-$10K Auto Send tier (MIS-1553/1555).
// Its sends are real, but calls/conversions/revenue are DELIBERATELY not
// tracked for this tier — the admin-api excludes it from call attribution — so
// its metric cells render an em-dash instead of "$0.00"/"0.0%" (which would
// read as "tracked and earned nothing").
const U10K_CAMPAIGN_ID = "__form_auto_send_u10k__";

function fmtPct(n: number): string {
  return `${((n ?? 0) * 100).toFixed(1)}%`;
}
function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}
function fmtUsd(cents: number): string {
  return `$${((cents ?? 0) / 100).toFixed(2)}`;
}

/**
 * Epoch SECONDS of the most recent America/Los_Angeles (Pacific) midnight at or
 * before `nowMs` (the start of "today" in PT). DST-correct on EVERY day,
 * INCLUDING the two transition days: we derive the PT CALENDAR DATE for `nowMs`,
 * then resolve the exact instant of 00:00:00 on that PT date by measuring PT's
 * actual UTC offset at that instant via Intl (never a fixed -7/-8, and never the
 * "subtract elapsed wall-clock seconds" trick that is off by 1h on a 23h/25h
 * transition day).
 */
function ptMidnightTodaySec(nowMs: number = Date.now()): number {
  const TZ = "America/Los_Angeles";
  // 1. Today's PT calendar date (Y-M-D) for the given instant.
  const dateParts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(nowMs));
  const dp = (t: string): number =>
    Number.parseInt(dateParts.find((p) => p.type === t)?.value ?? "0", 10);
  const y = dp("year");
  const mo = dp("month");
  const d = dp("day");

  // 2. PT's UTC offset (minutes) AT this instant — found by formatting `nowMs`
  //    in PT and comparing to the same wall-clock read as UTC. (PST=-480, PDT=-420.)
  const offsetMinutesAt = (ms: number): number => {
    const p = new Intl.DateTimeFormat("en-US", {
      timeZone: TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date(ms));
    const v = (t: string): number =>
      Number.parseInt(p.find((x) => x.type === t)?.value ?? "0", 10);
    // The wall-clock PT reading, interpreted as if it were UTC.
    const asUtc = Date.UTC(
      v("year"),
      v("month") - 1,
      v("day"),
      v("hour") % 24,
      v("minute"),
      v("second"),
    );
    return Math.round((asUtc - ms) / 60000);
  };

  // 3. Candidate PT-midnight epoch using the offset measured at `nowMs`, then a
  //    one-step correction using the offset measured AT the candidate (handles a
  //    transition occurring between midnight and now).
  const guessUtcMs = Date.UTC(y, mo - 1, d, 0, 0, 0);
  const off1 = offsetMinutesAt(nowMs);
  let midnightMs = guessUtcMs - off1 * 60000;
  const off2 = offsetMinutesAt(midnightMs);
  if (off2 !== off1) {
    midnightMs = guessUtcMs - off2 * 60000;
  }
  return Math.floor(midnightMs / 1000);
}

/** Resolve the selected preset/custom into a TimeSel for the API. */
function toTimeSel(
  presetSec: number,
  custom: { start: string; end: string } | null,
): TimeSel {
  if (custom && custom.start && custom.end) {
    const startSec = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const endSec = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { kind: "range", startSec, endSec };
  }
  if (presetSec === TODAY_PT) {
    // "Today (PT)" — [PT midnight today, now], epoch seconds (wire format).
    const now = Date.now();
    return {
      kind: "range",
      startSec: ptMidnightTodaySec(now),
      endSec: Math.floor(now / 1000),
    };
  }
  return { kind: "window", windowSec: presetSec };
}

/**
 * Flatten a TimeSel into the explicit { start, end } epoch-SECONDS pair the
 * `smsStats` route expects. A rolling window resolves to (now - windowSec, now);
 * an explicit range passes straight through. No ms conversion is introduced.
 */
function toSecondsRange(time: TimeSel): { start: number; end: number } {
  if (time.kind === "range") {
    return { start: time.startSec, end: time.endSec };
  }
  const end = Math.floor(Date.now() / 1000);
  return { start: end - time.windowSec, end };
}

export default function SmsDashboard() {
  const navigate = useNavigate();

  const [presetSec, setPresetSec] = useState<number>(TODAY_PT); // default: Today (PT)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);
  const [vertical, setVertical] = useState<string | null>(null);

  const [stats, setStats] = useState<SmsStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  const time = useMemo(() => toTimeSel(presetSec, custom), [presetSec, custom]);

  // A ref so the polling timer always calls the latest fetch (with current
  // filters) without re-arming on every keystroke.
  const fetchRef = useRef<() => void>(() => {});

  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      const { start, end } = toSecondsRange(time);
      // Omit `vertical` entirely when unset — the route param is optional and
      // the tsconfig uses exactOptionalPropertyTypes (no explicit `undefined`).
      const params = vertical ? { start, end, vertical } : { start, end };
      api
        .smsStats(params)
        .then((s) => {
          setStats(s);
          setError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLoading(false));
    },
    [time, vertical],
  );

  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the filters change.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-update polling — pauses when tab hidden or auto toggled off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  // The vertical dropdown is populated from the response, so it survives the
  // active filter (the server still returns every vertical's rollup).
  const verticalOptions = useMemo(() => {
    const rows = stats?.byVertical ?? [];
    const seen = new Set<string>();
    for (const v of rows) seen.add(v.vertical);
    if (vertical) seen.add(vertical);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [stats, vertical]);

  // --- Safe series locals --------------------------------------------------
  // Every nested array is read through `?? []` so a missing/null series in the
  // stats payload (empty or partial backend response) can NEVER throw a
  // `.length`/`.map` during render and blank the page.
  const overTime = useMemo(() => stats?.overTime ?? [], [stats]);
  const byHourOfDay = useMemo(() => stats?.byHourOfDay ?? [], [stats]);
  const byDayOfWeek = useMemo(() => stats?.byDayOfWeek ?? [], [stats]);
  const lagBuckets = useMemo(() => stats?.lagBuckets ?? [], [stats]);
  const u10kSends = useMemo(() => stats?.u10kSends ?? [], [stats]);

  // --- Chart data transforms (memoized to avoid double-render) -------------

  const lineSeries = useMemo<ChartLineSeries[]>(() => {
    return [
      {
        key: "sends",
        name: "Sends",
        data: overTime.map((r): LinePoint => ({ ts: r.day, value: r.sends })),
      },
      {
        key: "calls",
        name: "Attributed calls",
        data: overTime.map((r): LinePoint => ({ ts: r.day, value: r.attributedCalls })),
      },
      {
        key: "revenue",
        name: "Revenue ($)",
        data: overTime.map((r): LinePoint => ({ ts: r.day, value: r.revenueCents / 100 })),
      },
    ];
  }, [overTime]);

  const hourBars = useMemo<BarPoint[]>(() => {
    return byHourOfDay.map((r) => ({
      x: `${r.hourPt}:00`,
      sends: r.sends,
      calls: r.attributedCalls,
    }));
  }, [byHourOfDay]);

  const dowBars = useMemo<BarPoint[]>(() => {
    return byDayOfWeek.map((r) => ({
      x: DOW_LABELS[r.dowPt] ?? String(r.dowPt),
      sends: r.sends,
      calls: r.attributedCalls,
    }));
  }, [byDayOfWeek]);

  const lagBars = useMemo<BarPoint[]>(() => {
    return lagBuckets.map((b) => ({ x: b.label, count: b.count }));
  }, [lagBuckets]);

  // Under-$10K Auto Send tier (MIS-1555): daily VOLUME series only. This tier
  // is excluded from totals/overTime/rates by design (no call, conversion or
  // revenue tracking), so this dedicated series is the only place its send
  // volume is visible on the dashboard.
  const u10kLineSeries = useMemo<ChartLineSeries[]>(() => {
    return [
      {
        key: "sends",
        name: "Send attempts",
        data: u10kSends.map((r): LinePoint => ({ ts: r.day, value: r.sends })),
      },
      {
        key: "delivered",
        name: "Delivered",
        data: u10kSends.map((r): LinePoint => ({ ts: r.day, value: r.delivered })),
      },
    ];
  }, [u10kSends]);

  const campaignRows = useMemo(() => {
    const rows = stats?.byCampaign ?? [];
    return [...rows].sort((a, b) => b.revenueCents - a.revenueCents);
  }, [stats]);

  const verticalRows = useMemo(() => {
    const rows = stats?.byVertical ?? [];
    return [...rows].sort((a, b) => a.vertical.localeCompare(b.vertical));
  }, [stats]);

  const selectStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  const totals = stats?.totals ?? null;

  // The page is "empty" when stats loaded but every series is empty and there
  // are no sends — show one clean "No SMS data yet" state instead of a wall of
  // per-panel empties.
  const isEmpty =
    !loading &&
    !error &&
    !!stats &&
    (totals?.sends ?? 0) === 0 &&
    overTime.length === 0 &&
    byHourOfDay.length === 0 &&
    byDayOfWeek.length === 0 &&
    lagBuckets.length === 0 &&
    u10kSends.length === 0 &&
    campaignRows.length === 0;

  return (
    <div>
      <PageHeader
        title="SMS Dashboard"
        subtitle="SMS dispatch + attributed revenue"
        actions={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={vertical ?? "all"}
              onChange={(e) => setVertical(e.target.value === "all" ? null : e.target.value)}
              style={selectStyle}
              aria-label="Vertical"
            >
              <option value="all">All verticals</option>
              {verticalOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
              Auto
            </label>
            <button type="button" onClick={() => load(true)} disabled={loading} style={buttonStyle("ghost", loading)}>
              {loading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginBottom: tokens.space.md,
          }}
        >
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {vertical ? vertical : "all verticals"}
        </div>
      )}

      {/* Error banner — show the ApiError message; NEVER blank the page. When
          we already have stats from a prior poll, keep showing them and surface
          the error as a non-blocking banner. */}
      {error && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState
            title="Couldn’t refresh SMS stats"
            hint={
              stats
                ? `${error} — showing the last successful load.`
                : error
            }
          />
        </Card>
      )}

      {/* Empty state — stats loaded but there's nothing to show yet. */}
      {isEmpty && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState
            title="No SMS data yet"
            hint="No sends in this range. Adjust the time range or vertical filter, or enable Auto Send to start texting leads."
          />
        </Card>
      )}

      {/* Stat cards (main totals) */}
      {!isEmpty && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: tokens.space.lg,
            marginBottom: tokens.space.xl,
          }}
        >
          {loading && !totals ? (
            <>
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
            </>
          ) : totals ? (
            <>
              {/* Headline = real delivered sends (status === "sent"). The raw
                  ledger-row count (sends) is shown separately as "Send attempts"
                  so the misleading ~7K total is visible but clearly labeled as
                  attempts, not delivered messages. */}
              <StatCard label="Sent (delivered)" value={fmtCount(totals.delivered)} hint="messages delivered to carrier" emphasis />
              <StatCard label="Send attempts" value={fmtCount(totals.sends)} hint="delivered + failed" />
              <StatCard label="Failed" value={fmtCount(totals.failed)} hint="send failures" />
              <StatCard label="Attributed Calls" value={fmtCount(totals.attributedCalls)} hint="calls after a send" />
              <StatCard label="Revenue" value={fmtUsd(totals.revenueCents)} hint="attributed revenue" emphasis />
              <StatCard label="Call Rate" value={fmtPct(totals.callRate)} hint="calls / delivered" />
              <StatCard label="Conversion Rate" value={fmtPct(totals.conversionRate)} hint="conversions / delivered" />
              <StatCard label="Revenue per Send" value={fmtUsd(totals.revenuePerSend)} hint="revenue / delivered" />
            </>
          ) : null}
        </div>
      )}

      {/* Over-time daily trend */}
      {!isEmpty && (
        <Card title="Over time — sends, attributed calls, revenue" style={{ marginBottom: tokens.space.xl }}>
          {loading && !stats ? (
            <Skeleton height={260} />
          ) : overTime.length > 0 ? (
            <ChartLine series={lineSeries} height={260} />
          ) : (
            <EmptyState title="No data in this range" hint="Adjust the time range or vertical filter." />
          )}
        </Card>
      )}

      {/* Time-of-day distributions */}
      {!isEmpty && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
            gap: tokens.space.xl,
            marginBottom: tokens.space.xl,
          }}
        >
          <Card title="By hour of day (PT)">
            {loading && !stats ? (
              <Skeleton height={300} />
            ) : byHourOfDay.length > 0 ? (
              <ChartBar
                data={hourBars}
                bars={[
                  { key: "sends", name: "Sends" },
                  { key: "calls", name: "Calls" },
                ]}
                height={300}
              />
            ) : (
              <EmptyState title="No data" hint="No sends bucketed by hour yet." />
            )}
          </Card>
          <Card title="By day of week (PT)">
            {loading && !stats ? (
              <Skeleton height={240} />
            ) : byDayOfWeek.length > 0 ? (
              <ChartBar
                data={dowBars}
                bars={[
                  { key: "sends", name: "Sends" },
                  { key: "calls", name: "Calls" },
                ]}
                height={240}
              />
            ) : (
              <EmptyState title="No data" hint="No sends bucketed by day yet." />
            )}
          </Card>
        </div>
      )}

      {/* Lag-to-call histogram */}
      {!isEmpty && (
        <Card title="Lag to call" style={{ marginBottom: tokens.space.xl }}>
          {loading && !stats ? (
            <Skeleton height={200} />
          ) : lagBuckets.length > 0 ? (
            <ChartBar
              data={lagBars}
              bars={[{ key: "count", name: "Calls" }]}
              layout="horizontal"
              height={200}
              showValues
            />
          ) : (
            <EmptyState title="No attributed calls" hint="No call lag to histogram in this range." />
          )}
        </Card>
      )}

      {/* Best-hour callout */}
      {!isEmpty && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: tokens.space.md, flexWrap: "wrap" }}>
            <div
              style={{
                fontSize: tokens.font.size.xl,
                fontWeight: 700,
                color: tokens.color.tealLight,
              }}
            >
              {stats && stats.bestHourPt !== null && stats.bestHourPt !== undefined
                ? `Best hour: ${stats.bestHourPt}:00 PT`
                : "Insufficient data"}
            </div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
              (highest revenue-per-send with ≥20 delivered)
            </div>
          </div>
        </Card>
      )}

      {/* Under-$10K Auto Send tier — dedicated VOLUME-ONLY chart (MIS-1555).
          These sends are deliberately excluded from the totals and every chart
          above (no call/conversion/revenue tracking for this tier), so this is
          the only chart where the operator can see how many went out. An empty
          series is the NORMAL state while the tier is disabled — show a short
          explanation, never a broken/blank chart. */}
      {!isEmpty && (
        <Card
          title="Under $10K — sends only (no call or revenue tracking)"
          style={{ marginBottom: tokens.space.xl }}
        >
          {loading && !stats ? (
            <Skeleton height={240} />
          ) : u10kSends.length > 0 ? (
            <ChartLine series={u10kLineSeries} height={240} />
          ) : (
            <EmptyState
              title="No under-$10K sends in this range"
              hint="Normal while the under-$10K Auto Send tier is disabled — daily send volume appears here once it starts sending. Calls and revenue are not tracked for this tier by design."
            />
          )}
        </Card>
      )}

      {/* Per-campaign breakdown */}
      {!isEmpty && (
        <Card padding={0} style={{ marginBottom: tokens.space.xl }}>
          <div
            style={{
              padding: tokens.space.lg,
              borderBottom: `1px solid ${tokens.color.border}`,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: tokens.font.size.md }}>Per-campaign breakdown</div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
              Sorted by revenue · click a row for campaign detail
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
                whiteSpace: "nowrap",
              }}
            >
              <thead>
                <tr>
                  <Th>Campaign</Th>
                  <Th>Vertical</Th>
                  <Th align="right">Sent</Th>
                  <Th align="right">Attempts</Th>
                  <Th align="right">Calls</Th>
                  <Th align="right">Converted</Th>
                  <Th align="right">Revenue</Th>
                  <Th align="right">Call rate</Th>
                  <Th align="right">Conv. rate</Th>
                  <Th align="right">Rev / send</Th>
                </tr>
              </thead>
              <tbody>
                {campaignRows.length === 0 ? (
                  <tr>
                    <Td>{loading ? "Loading…" : "No data"}</Td>
                    <Td>—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                  </tr>
                ) : (
                  campaignRows.map((c, idx) => (
                    <tr
                      key={c.campaign_id}
                      onClick={() => navigate(`/sms/campaigns/${encodeURIComponent(c.campaign_id)}`)}
                      style={{
                        cursor: "pointer",
                        background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                      }}
                    >
                      <Td>{c.campaign_name}</Td>
                      <Td>{c.vertical}</Td>
                      <RollupCells row={c} untracked={c.campaign_id === U10K_CAMPAIGN_ID} />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Per-vertical breakdown */}
      {!isEmpty && (
        <Card padding={0}>
          <div
            style={{
              padding: tokens.space.lg,
              borderBottom: `1px solid ${tokens.color.border}`,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: tokens.font.size.md }}>Per-vertical breakdown</div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>Sorted A–Z</div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
                whiteSpace: "nowrap",
              }}
            >
              <thead>
                <tr>
                  <Th>Vertical</Th>
                  <Th align="right">Sent</Th>
                  <Th align="right">Attempts</Th>
                  <Th align="right">Calls</Th>
                  <Th align="right">Converted</Th>
                  <Th align="right">Revenue</Th>
                  <Th align="right">Call rate</Th>
                  <Th align="right">Conv. rate</Th>
                  <Th align="right">Rev / send</Th>
                </tr>
              </thead>
              <tbody>
                {verticalRows.length === 0 ? (
                  <tr>
                    <Td>{loading ? "Loading…" : "No data"}</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                    <Td align="right">—</Td>
                  </tr>
                ) : (
                  verticalRows.map((v, idx) => (
                    <tr
                      key={v.vertical}
                      style={{ background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)" }}
                    >
                      <Td>{v.vertical}</Td>
                      <RollupCells row={v} />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// Local alias so the memo return type stays readable.
type ChartLineSeries = {
  key: string;
  name: string;
  color?: string;
  data: LinePoint[];
};

/**
 * Render the shared rollup metric cells (sent → revenue-per-send). Both the
 * per-campaign and per-vertical tables reuse this so the columns stay in sync;
 * `row` is anything that extends `SmsRollup`.
 *
 * "Sent" is the REAL delivered count (status === "sent"); the raw ledger-row
 * count is shown separately as "Attempts" so the misleading total is visible
 * but clearly labeled. "Converted" is the count of revenue-bearing calls.
 *
 * `untracked` (the under-$10K Auto Send pseudo-campaign, MIS-1555): calls,
 * conversions and revenue are structurally zero because the tier is not
 * tracked — render "—" for those cells so a zero doesn't read as "tracked and
 * earned nothing". Sent/Attempts stay real numbers.
 */
function RollupCells({ row, untracked = false }: { row: SmsRollup; untracked?: boolean }) {
  const dash = "—";
  return (
    <>
      <Td align="right">{fmtCount(row.delivered)}</Td>
      <Td align="right">{fmtCount(row.sends)}</Td>
      <Td align="right">{untracked ? dash : fmtCount(row.attributedCalls)}</Td>
      <Td align="right">{untracked ? dash : fmtCount(row.conversions)}</Td>
      <Td align="right">{untracked ? dash : fmtUsd(row.revenueCents)}</Td>
      <Td align="right">{untracked ? dash : fmtPct(row.callRate)}</Td>
      <Td align="right">{untracked ? dash : fmtPct(row.conversionRate)}</Td>
      <Td align="right">{untracked ? dash : fmtUsd(row.revenuePerSend)}</Td>
    </>
  );
}

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" | "center" }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
